import { ResponseTranslation, Translation } from '../translation';

/** Global.Models ApproverType. */
export enum ApproverType {
  User = 0,
  Role = 1,
}

/** The Global.Models GlobalEntityDefinition codes an approval policy can apply to (#5762). */
export enum ApprovalEntityType {
  Trip = 66,
  EmployeeAdvance = 67,
  FundMovement = 68,
  ExpenseClaim = 69,
}

export interface ApprovalEntityTypeOption {
  value: ApprovalEntityType;
  labelKey: string;
  fallback: string;
}

export const APPROVAL_ENTITY_TYPES: ApprovalEntityTypeOption[] = [
  { value: ApprovalEntityType.Trip, labelKey: 'ApprovalPolicies_Entity_Trip', fallback: 'Trip' },
  { value: ApprovalEntityType.EmployeeAdvance, labelKey: 'ApprovalPolicies_Entity_EmployeeAdvance', fallback: 'Employee advance' },
  { value: ApprovalEntityType.FundMovement, labelKey: 'ApprovalPolicies_Entity_FundMovement', fallback: 'Fund movement' },
  { value: ApprovalEntityType.ExpenseClaim, labelKey: 'ApprovalPolicies_Entity_ExpenseClaim', fallback: 'Expense claim' },
];

export interface ApprovalPolicyApprover {
  type: ApproverType;
  /** Username when type is User, role name when Role. */
  value: string;
  /** Null = parallel. */
  order: number | null;
  isRequired: boolean;
  /** Null = always applies. */
  fromAmountUsd: number | null;
}

export interface ApprovalPolicy {
  id: string;
  name: ResponseTranslation;
  isActive: boolean;
  minimumApprovals: number;
  approvers: ApprovalPolicyApprover[];
  appliesTo: ApprovalEntityType[];
  defaultEntities: ApprovalEntityType[];
  createdBy?: string | null;
  createdDate?: string | null;
  modifiedBy?: string | null;
  modifiedDate?: string | null;
}

/** Body of POST and PUT: every editable field. */
export interface ApprovalPolicyUpdate {
  /** Both languages required to save. Null only in a preview of a form whose English name is still blank. */
  name: Translation | null;
  isActive: boolean;
  minimumApprovals: number;
  approvers: ApprovalPolicyApprover[];
  appliesTo: ApprovalEntityType[];
  defaultEntities: ApprovalEntityType[];
}

export interface ApprovalPolicySearchCommand {
  name?: string | null;
  isActive?: boolean | null;
  appliesTo?: ApprovalEntityType[] | null;
  defaultFor?: ApprovalEntityType[] | null;
}

export interface PagedResult<T> {
  items: T[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
}

// ---- POST approval-policy/preview

export interface ApprovalPolicyPreviewCommand {
  id: string | null;
  policy: ApprovalPolicyUpdate;
  usdAmount: number | null;
  requesterUsername: string | null;
}

export interface ApprovalPolicyPreviewMessage extends ResponseTranslation {
  field: string;
}

export interface ApprovalPolicyBand extends ResponseTranslation {
  fromAmountUsd: number;
  approvers: ApprovalPolicyApprover[];
  eligibleOptionalCount: number;
  canBeMet: boolean;
}

export interface ApprovalPolicyDefaultMove {
  entityDefinitionCode: ApprovalEntityType;
  fromPolicyId: string;
  fromPolicyName: ResponseTranslation;
}

export interface ApprovalSlot {
  id: string;
  type: ApproverType;
  value: string;
  order: number | null;
  isRequired: boolean;
  fromAmountUsd: number | null;
}

export interface FrozenApproval {
  policyReference: string;
  policyName: ResponseTranslation;
  evaluatedUsdAmount: number;
  minimumApprovals: number;
  slots: ApprovalSlot[];
  isAutoApproved: boolean;
  systemStamp: { at: string; reason: string } | null;
}

export interface ApprovalPolicySimulation {
  approval: FrozenApproval | null;
  error: ApprovalPolicyPreviewMessage | null;
}

export interface ApprovalPolicyPreview {
  isValid: boolean;
  errors: ApprovalPolicyPreviewMessage[];
  bands: ApprovalPolicyBand[];
  defaultMoves: ApprovalPolicyDefaultMove[];
  simulation: ApprovalPolicySimulation | null;
}
